import { motion } from 'motion/react';
import { QuestionRenderer } from '@/components/question-types/QuestionRenderer';
import type { NewQuestion } from '@/types';
import EyeIcon from '@/assets/common/eye.svg?react';
import styles from './QuestionPreviewPanel.module.scss';

interface QuestionPreviewPanelProps {
  question: Partial<NewQuestion> | null;
}

export function QuestionPreviewPanel({ question }: QuestionPreviewPanelProps) {
  if (!question || !question.question_text || !question.content) {
    return (
      <div className={styles.preview}>
        <div className={styles.preview__empty}>
          <motion.div
            className={styles.preview__emptyIcon}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          >
            <EyeIcon width={48} height={48} />
          </motion.div>
          <h3 className={styles.preview__emptyTitle}>Preview</h3>
          <p className={styles.preview__emptyText}>
            Your question preview will appear here as you build it
          </p>
        </div>
      </div>
    );
  }

  // Convert Partial<NewQuestion> to Question for preview
  // We need to add temporary ids for preview purposes
  const previewQuestion = {
    id: 'preview',
    quizlet_id: 'preview',
    question_text: question.question_text,
    question_type: question.question_type!,
    content: question.content,
    explanation: question.explanation || '',
    difficulty: question.difficulty || 'medium',
    order_index: 0,
    tags: question.tags || [],
  };

  return (
    <motion.div
      className={styles.preview}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <div className={styles.preview__header}>
        <h3 className={styles.preview__title}>Preview</h3>
        <span className={styles.preview__badge}>
          {question.question_type?.replace('_', ' ')}
        </span>
      </div>

      <div className={styles.preview__content}>
        <QuestionRenderer
          question={previewQuestion as any}
          userAnswer={null}
          onAnswer={() => { }}
          showExplanation={false}
          isReviewMode={true}
        />
      </div>

      {question.explanation && (
        <div className={styles.preview__explanation}>
          <h4 className={styles.preview__explanationTitle}>Explanation</h4>
          <p className={styles.preview__explanationText}>{question.explanation}</p>
        </div>
      )}
    </motion.div>
  );
}
